import { ApplicationConfig, APP_INITIALIZER, Injectable } from "@angular/core";
import { DatePipe } from "@angular/common";
import {
  provideRouter,
  withHashLocation,
  withPreloading,
  PreloadAllModules,
} from "@angular/router";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { environment } from "../environments/environment";
import { provideHighcharts } from "highcharts-angular";
import { providePrimeNG } from "primeng/config";
import Aura from "@primeuix/themes/aura";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { MessageService } from "primeng/api";

import { ROUTES } from "./app.routes";
import { AppInterceptor } from "./app.interceptor";
import { AppGuard } from "./app.guard";
import { LoginService } from "./pages/login/login.service";
import { ResetpasswordService } from "./pages/resetpassword/resetpassword.service";
import { Globals } from "./models/lib/globals";
import { CommonGrid } from "./models/lib/CommonGrid";
import { CSVConverterService } from "./services/csvconverter.service";

import {
  AccountService,
  API_BASE_URL,
  CustomerService,
  LocationService,
  RoleService,
  PartService,
  FileService,
  InvoiceService,
  WorkOrderService,
  TimezoneService,
  PurchaseOrderService,
  AuditDetailsService,
  ReportService,
  SearchService,
  ProcedureService,
  DocumentService,
  XmlService,
  UserService,
} from "./services/api.client.generated";
import { IpService } from "./services/ip-address.service";

// Define Aura preset with Sky primary and Zinc surface for dark mode
const AuraSkyPreset = {
  ...Aura,
  semantic: {
    ...Aura.semantic,
    primary: Aura.primitive!.sky,
    colorScheme: {
      ...Aura.semantic!.colorScheme,
      dark: {
        ...Aura.semantic!.colorScheme?.dark,
        surface: Aura.primitive!.zinc,
      },
    },
  },
  components: {
    ...Aura.components,
    tooltip: {
      ...Aura.components?.tooltip,
      colorScheme: {
        light: {
          root: { background: "{purple.500}", color: "rgb(59, 7, 100)" },
        },
        dark: {
          root: { background: "{purple.400}", color: "rgb(59, 7, 100)" },
        },
      },
    },
  },
};

function initializeIconLibrary(library: FaIconLibrary) {
  return () => {
    library.addIconPacks(fas, far, fab);
  };
}

// 44398
const hostApi = environment.url;
const portApi = environment.production ? 5000 : 443;
const baseURLApi = "";

@Injectable()
export class AppConfig {
  config = {
    remote: "https://flatlogic-node-backend.herokuapp.com",
    isBackend: environment.backend,
    hostApi,
    portApi,
    baseURLApi,
    auth: {
      email: "admin@flatlogic.com",
      password: "password",
    },
    settings: {
      colors: {
        white: "#fff",
        black: "#000",
        "gray-light": "#999",
        "gray-lighter": "#eee",
        gray: "#666",
        "gray-dark": "#343434",
        "gray-darker": "#222",
        "gray-semi-light": "#777",
        "gray-semi-lighter": "#ddd",
        "brand-primary": "#5d8fc2",
        "brand-success": "#64bd63",
        "brand-warning": "#f0b518",
        "brand-danger": "#dd5826",
        "brand-info": "#5dc4bf",
      },
    },
  };

  getConfig(): Object {
    return this.config;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(ROUTES, withHashLocation(), withPreloading(PreloadAllModules)),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    provideHighcharts(),
    providePrimeNG({
      theme: {
        preset: AuraSkyPreset,
        options: {
          prefix: "p",
          darkModeSelector: ".app-dark",
          cssLayer: false,
        },
      },
      ripple: true,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeIconLibrary,
      deps: [FaIconLibrary],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
      multi: true,
    },
    { provide: API_BASE_URL, useValue: environment.url },
    MessageService,
    DatePipe,
    LoginService,
    AppGuard,
    AppConfig,
    ResetpasswordService,
    Globals,
    CommonGrid,
    CSVConverterService,
    AccountService,
    PartService,
    FileService,
    UserService,
    IpService,
    InvoiceService,
    CustomerService,
    TimezoneService,
    WorkOrderService,
    AuditDetailsService,
    LocationService,
    RoleService,
    PurchaseOrderService,
    ReportService,
    SearchService,
    ProcedureService,
    DocumentService,
    XmlService,
  ],
};
